import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import SharedScheduleList from '../../components/Schedules/ScheduleList';
import { useStoreActions, useStoreState } from '../../models/RootStore';

const MobileScheduleList: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ bacnetUnit?: any; restoreSelectedItem?: any }>();
  const match = useRouteMatch<{
    unitId?: string;
    groupId?: string;
    systemId?: string;
    sensorId?: string;
  }>();
  const { unitId, groupId, systemId, sensorId } = match.params;
  const bacnetUnit = location.state?.bacnetUnit;

  const [item, setItem] = useState<any>(null);
  const [allSchedules, setAllSchedules] = useState<any>({});
  const [isLoading, setLoading] = useState(true);

  const getUnit = useStoreState((s) => s.units.getUnit);
  const getGroup = useStoreActions((a) => a.groups.getGroup);
  const getSystem = useStoreState((s) => s.systems.getLocalSystem);
  const { getObjectSchedules } = useStoreActions((a) => a.schedules);
  const { addMessage } = useStoreActions((a) => a.errorMessage);
  const { _storeUpdateUnit } = useStoreActions((a) => a.units);
  const { temperatureScaleMirror } = useStoreState((s) => s);
  const { temperatureScale = 1 } = useStoreState((s) => s.users.me);
  const isCelsius = +temperatureScaleMirror.celsius === +temperatureScale;

  const goBack = () => {
    const restoreSelectedItem = bacnetUnit
      ? { type: 'bacnetUnit', id: bacnetUnit.id, isGroup: false }
      : location.state?.restoreSelectedItem;

    if (restoreSelectedItem) {
      history.push('/control', { restoreSelectedItem });
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (bacnetUnit || sensorId) {
      setLoading(false);
      return;
    }

    if (!unitId && !groupId && !systemId) {
      history.push('/');
      return;
    }

    const fetchData = async () => {
      try {
        let fetchedItem: any;
        let schedules: any = {};

        if (groupId) {
          fetchedItem = await getGroup(groupId);
          schedules = await getObjectSchedules({
            objectType: 'group',
            id: groupId,
          });
        } else if (systemId) {
          fetchedItem = getSystem(systemId);
          schedules = await getObjectSchedules({
            objectType: 'system',
            id: systemId,
          });
        } else if (unitId) {
          fetchedItem = getUnit(unitId);
          if (!fetchedItem) {
            history.push('/');
            return;
          }
          schedules = await getObjectSchedules({
            objectType: 'unit',
            id: unitId,
            type: '0',
          });
        }

        setItem(fetchedItem);
        setAllSchedules(schedules || {});
      } catch (e: any) {
        addMessage({ message: e.message });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const addItemSchedule = (
    isGroup: boolean,
    itemId: string,
    scheduleId: string
  ) => {
    setItem((prev: any) => ({
      ...prev,
      schedules: [...(prev.schedules || []), scheduleId],
    }));
    if (!isGroup && unitId) {
      const unit = getUnit(unitId);
      unit &&
        _storeUpdateUnit({
          id: itemId,
          data: { ...unit, schedules: [...(unit.schedules || []), scheduleId] },
        });
    }
  };

  const deleteItemSchedule = (
    isGroup: boolean,
    itemId: string,
    scheduleId: string
  ) => {
    setItem((prev: any) => ({
      ...prev,
      schedules: (prev.schedules || []).filter((id: string) => id !== scheduleId),
    }));
    if (!isGroup && unitId) {
      const unit = getUnit(unitId);
      if (unit) {
        _storeUpdateUnit({
          id: itemId,
          data: {
            ...unit,
            schedules: (unit.schedules || []).filter(
              (id: string) => id !== scheduleId
            ),
          },
        });
      }
    }
  };

  if (bacnetUnit) {
    return (
      <SharedScheduleList
        mode="bacnet"
        bacnetUnit={bacnetUnit}
        isDesktop={false}
        hideSchedulesPanel={goBack}
      />
    );
  }

  if (sensorId) {
    return (
      <SharedScheduleList
        mode="sensor"
        sensorId={sensorId}
        hideSchedulesPanel={goBack}
      />
    );
  }

  if (isLoading || !item) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <SharedScheduleList
      mode="unit"
      item={item}
      allSchedules={allSchedules}
      setAllSchedules={setAllSchedules}
      addItemSchedule={addItemSchedule}
      deleteItemSchedule={deleteItemSchedule}
      isGroup={!!groupId}
      isHalfCDegreeEnabled={item?.isHalfCDegreeEnabled && isCelsius}
      hideSchedulesPanel={goBack}
    />
  );
};

export default MobileScheduleList;
